import React from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import { useLanguageContext } from '../context/LanguageContext';
import { translations } from '../translations';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

const Experience = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { language } = useLanguageContext();
  const t = translations[language];

  const ExperienceCard = ({ position, isLast }) => (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        backgroundColor: theme.palette.background.paper,
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      <Typography variant="h6" component="h3" color="primary" gutterBottom>
        {position.title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <BusinessIcon 
          sx={{ 
            mr: 1, 
            color: 'primary.main',
            width: { xs: '1.5rem', sm: '2rem' },
            height: { xs: '1.5rem', sm: '2rem' },
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'rotate(10deg) scale(1.1)',
            },
          }} 
        />
        <Typography variant="subtitle1" color="text.secondary">
          {position.company}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <LocationOnIcon 
          sx={{ 
            mr: 1, 
            color: 'primary.main',
            width: { xs: '1.5rem', sm: '2rem' },
            height: { xs: '1.5rem', sm: '2rem' },
            transition: 'transform 0.2s ease-in-out',
            '&:hover': {
              transform: 'rotate(10deg) scale(1.1)',
            },
          }} 
        />
        <Typography variant="subtitle2" color="text.secondary">
          {position.location}
        </Typography>
      </Box>

      <Chip
        label={position.period}
        color="primary"
        variant="outlined"
        sx={{
          mb: 2,
          transition: 'transform 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        }}
      />

      <List>
        {position.responsibilities.map((responsibility, idx) => (
          <ListItem 
            key={idx}
            sx={{
              transition: 'transform 0.2s ease-in-out, background-color 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateX(10px)',
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <ArrowRightIcon 
              sx={{ 
                mr: 1, 
                color: 'primary.main',
                width: { xs: '1.5rem', sm: '2rem' },
                height: { xs: '1.5rem', sm: '2rem' },
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'rotate(10deg) scale(1.1)',
                },
              }} 
            />
            <ListItemText 
              primary={responsibility}
              primaryTypographyProps={{
                style: {
                  fontSize: '0.9rem',
                }
              }}
            />
          </ListItem>
        ))}
      </List>
      {!isLast && isMobile && <Divider sx={{ mt: 3 }} />}
    </Paper>
  );

  return (
    <Box sx={{ py: 6 }}>
      <Box 
        sx={{ 
          mb: { xs: 4, sm: 5, md: 6 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Typography
          variant="h3"
          component="h2"
          align="center"
          gutterBottom
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
            fontWeight: 'bold'
          }}
        >
          {isMobile && (
            <WorkIcon 
              sx={{ 
                color: 'primary.main',
                width: { xs: '1.5rem', sm: '2rem' },
                height: { xs: '1.5rem', sm: '2rem' },
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'rotate(10deg) scale(1.1)',
                },
              }} 
            />
          )}
          {t.about.experience.title}
        </Typography>
      </Box>

      {isMobile ? (
        <Stack spacing={3}>
          {t.about.experience.positions.map((position, index) => (
            <ExperienceCard 
              key={index} 
              position={position} 
              isLast={index === t.about.experience.positions.length - 1}
            />
          ))}
        </Stack>
      ) : (
        <Timeline position="alternate-reverse">
          {t.about.experience.positions.map((position, index) => (
            <TimelineItem key={index}>
              <TimelineSeparator>
                <TimelineDot
                  sx={{
                    bgcolor: 'primary.main',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'scale(1.2) rotate(10deg)',
                    },
                  }}
                >
                  <WorkIcon />
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <ExperienceCard position={position} />
              </TimelineContent>
            </TimelineItem>
          ))}
        </Timeline>
      )}
    </Box>
  );
};

export default Experience;
